import { orderBy } from "lodash";
import { AdminUsersContext } from "@app/admin/contexts/AdminUsersContext";
import { MultiselectItem, MultiselectOnChangeResponse, QESMultiSelect } from "@app/shared/qes-standards/forms/Multiselect";
import { CheckboxStateEnum } from "@app/shared/qes-standards/forms/Checkbox";
import { ColumnConfig, wafflesKeyColumnName } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { FlyoutContext, FlyoutFooter } from "@app/shared/Flyouts";
import { FC, JSX, useContext, useEffect, useRef, useState } from "react";
import { Accordion } from "@app/shared/Accordion";
import { AccordionContainer } from "@app/shared/AccordionContainer";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { ClientService } from "@app/client/clientService";
import { RoleGroupModel } from "@app/admin/role-groups/roleGroup";
import RoleGroupService from "@app/admin/role-groups/roleGroupService";

interface RolePreviewTableData {
  [wafflesKeyColumnName]: string;
  role: string;
  assignedBy: JSX.Element;
}

const rolePreviewColumnConfig: ColumnConfig = {
  assignedBy: {
    displayText: "Assigned By",
    testId: "assignedBy",
  },
  role: {
    displayText: "Role",
    testId: "role"
  },
};

interface GuestLoginAdminPanelProps {
  guestClientId?: number;
  guestClientName?: string;
  guestClientAssignedRoles?: string[];
  guestClientAssignedRoleGroups?: number[];
  guestUserId?: number;
}

export const GuestLoginAdminPanelContent: FC<GuestLoginAdminPanelProps> = (props: GuestLoginAdminPanelProps) => {
  const flyoutContext = useContext(FlyoutContext);
  const adminUsersContext = useContext(AdminUsersContext);
  const clientService = useRef(new ClientService()).current;
  const [availableRoleGroupOptions, setAvailableRoleGroupOptions] = useState<Record<string, MultiselectItem>>({});
  const [availableRoleGroups, setAvailableRoleGroups] = useState<RoleGroupModel[]>([]);
  const [availableRoles, setAvailableRoles] = useState<Record<string, MultiselectItem>>();
  const [availableRoleNames, setAvailableRoleNames] = useState<string[]>();
  const [clientOptions, setClientOptions] = useState<JSX.Element[]>([]);
  const [questViewerRoles, setQuestViewerRoles] = useState<string[]>();
  const [resourceAdminRoles, setResourceAdminRoles] = useState<string[]>();
  const [rolePreview, setRolePreview] = useState<RolePreviewTableData[]>([]);
  const [selectedClientId, setSelectedClientId] = useState(props.guestClientId);
  const [selectedRoleGroupIds, setSelectedRoleGroupIds] = useState<number[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<string[]>();
  const [userDefaultRoles, setUserDefaultRoles] = useState<string[]>();

  useEffect(() => {
    if (props.guestClientId) {
      getAvailableClientRoles(props.guestClientId);
      updateGuestLoginRoles(props.guestClientId);
      getAvailableRoleGroups(props.guestClientId);
    }
  }, []);

  useEffect(() => {
    const newOptions = buildClientOptions();
    setClientOptions(newOptions);
    setSelectedClientId(props.guestClientId);

    if (props.guestClientId) {
      getAvailableClientRoles(props.guestClientId);
      updateGuestLoginRoles(props.guestClientId);
      getAvailableRoleGroups(props.guestClientId);
    }
  }, [props.guestClientName, props.guestClientId, adminUsersContext.allClients]);

  useEffect(() => {
    let updatePreview = false;
    if (selectedRoles && availableRoleNames) {
      const updatedRoleOptions: { [key: string]: MultiselectItem } = {};
      availableRoleNames?.forEach((name) => {
        updatedRoleOptions[name] = {
          currentState: selectedRoles.includes(name) ? CheckboxStateEnum.Checked : CheckboxStateEnum.Unchecked,
          displayValue: name,
        }
      });
      setAvailableRoles(updatedRoleOptions);
      updatePreview = true;
    }

    if (selectedRoleGroupIds && availableRoleGroups) {
      const updatedRoleGroupOptions: { [key: string]: MultiselectItem } = {};
      availableRoleGroups?.forEach((rg) => {
        updatedRoleGroupOptions[rg.name] = {
          currentState: selectedRoleGroupIds.includes(rg.roleGroupId) ? CheckboxStateEnum.Checked : CheckboxStateEnum.Unchecked,
          displayValue: rg.name,
        }
      });
      setAvailableRoleGroupOptions(updatedRoleGroupOptions);
      updatePreview = true;
    }

    if (updatePreview) {
      createRolePreviewWafflesTable(selectedRoles, selectedRoleGroupIds);
    }
  }, [selectedRoles, availableRoleNames, selectedRoleGroupIds, availableRoleGroups]);

  const buildClientOptions = () => {
    if (props.guestClientId) {
      // the user already exists and the client cannot be changed
      return [<option value={props.guestClientId} key={props.guestClientId}>{props.guestClientName}</option>];
    }
    // filter out inactive clients
    // filter out clientId 0 always and clientId 1 when editing guest logins for non-Quest users
    // filter out the current user's client id
    // filter out any clientids that already have current guest users
    return [<option value="-1" key="-1" />, ...adminUsersContext.allClients.filter((c) => c.isActive && c.clientId > (adminUsersContext.clientId === 1 ? 0 : 1) && c.clientId !== adminUsersContext.clientId && !adminUsersContext.guestLogins.find((gl) => gl.clientId === c.clientId)).map((client) => <option value={client.clientId} key={client.clientId}>{client.name}</option>)];
  }

  const createRolePreviewWafflesTable = (selectedRoles: string[], selectedRoleGroupIds: number[]) => {
    const roleDict: { [key: string]: { assignedBy: string[], name: string } } = {};
    if (!!selectedRoles) {
      for (const role of selectedRoles) {
        roleDict[role] = { assignedBy: ["Individually Assigned"], name: role };
      }
    }

    if (!!selectedRoleGroupIds) {
      const selectedRoleGroups = availableRoleGroups.filter(x => selectedRoleGroupIds.includes(x.roleGroupId));
      for (const roleGroup of orderBy(selectedRoleGroups, x => x.name, "asc")) {
        for (const role of roleGroup.roles) {
          if (!!roleDict[role.name]) {
            roleDict[role.name].assignedBy.push(roleGroup.name);
          } else {
            roleDict[role.name] = { assignedBy: [roleGroup.name], name: role.name };
          }
        }
      }
    }

    const preview: RolePreviewTableData[] = orderBy(Object.values(roleDict), rd => rd.name, "asc").map(rd => {
      /* oxlint-disable sort-keys */
      return {
        key: rd.name,
        role: rd.name,
        assignedBy:
          <>
            {rd.assignedBy.map((x, i) => <div key={i}>{x}<br /></div>)}
          </>
      }
      /* oxlint-enable sort-keys */
    });

    setRolePreview(preview);
  }

  const isValid = () => {
    return selectedClientId && isRoleValid();
  }

  const isRoleValid = () => {
    return (selectedRoles && selectedRoles.length > 0) || (selectedRoleGroupIds && selectedRoleGroupIds.length > 0);
  }

  const handleOnClick = () => {
    if (props.guestClientId) {
      // update the roles for the already-existing guest user id
      adminUsersContext.updateGuestLogin(props.guestClientId, orderBy(selectedRoles, (r) => r.toLowerCase(), "asc"), selectedRoleGroupIds)

    } else {
      // add a guest login in the specified client to the specified user 
      adminUsersContext.addGuestLogin(selectedClientId, adminUsersContext.allClients.find((c) => c.clientId == selectedClientId).name, orderBy(selectedRoles, (r) => r.toLowerCase(), "asc"), selectedRoleGroupIds);
    }
    flyoutContext.handleCloseFlyout();
  }

  // when the selected client changes, set the selectedClientId in state, load the roles for that client id and set those roles in state as well. 
  const handleClientChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const clientId = parseInt(e.target.value, 10);

    setSelectedClientId(clientId);

    if (clientId) {
      // load all available and assigned roles for the selected client
      getAvailableClientRoles(clientId);
      updateGuestLoginRoles(clientId);
      getAvailableRoleGroups(clientId);
    }
  }

  // get the assigned roles for this client and set them in state
  const getAvailableClientRoles = (clientId: number) => {
    clientService.getClientRoles(clientId).then((roles) => {

      const roleOptions: { [key: string]: MultiselectItem } = {};
      const newAvailableRoleNames: string[] = [];
      const newUserDefaultRoles: string[] = [];
      const newResourceAdminRoles: string[] = [];
      const newQuestViewerRoles: string[] = [];
      orderBy(roles, (r) => r.name.toLowerCase(), "asc").forEach((r) => {
        newAvailableRoleNames.push(r.name);
        Object.assign(roleOptions, {
          [r.name]: {
            currentState: (selectedRoles && selectedRoles.find((sr) => sr === r.name)) ? CheckboxStateEnum.Checked : CheckboxStateEnum.Unchecked,
            displayValue: r.name,
          },
        });
        if (r.userDefault === true)
          newUserDefaultRoles.push(r.name);
        if (r.resourceAdminGuestDefault === true)
          newResourceAdminRoles.push(r.name);
        if (r.questViewerGuestDefault === true)
          newQuestViewerRoles.push(r.name);
      });

      setAvailableRoleNames(newAvailableRoleNames);
      setAvailableRoles(roleOptions);
      setQuestViewerRoles(newQuestViewerRoles);
      setResourceAdminRoles(newResourceAdminRoles);
      setUserDefaultRoles(newUserDefaultRoles);
    });
  }

  const getAvailableRoleGroups = (clientId: number) => {
    const roleGroupsService = new RoleGroupService();
    roleGroupsService.getRoleGroupsByClientId(clientId, true, false, false, true, 0, -1).then((roleGroups) => {
      const roleGroupOptions: { [key: string]: MultiselectItem } = {};
      const newAvailableRoleGroups: RoleGroupModel[] = [];

      orderBy(roleGroups.roleGroupList, (rg) => rg.name.toLowerCase(), "asc").forEach((rg) => {
        newAvailableRoleGroups.push(rg);
        Object.assign(roleGroupOptions, {
          [rg.name]: {
            currentState: (newSelectedRoleGroupIds && newSelectedRoleGroupIds.find((srg) => srg === rg.roleGroupId)) ? CheckboxStateEnum.Checked : CheckboxStateEnum.Unchecked,
            displayValue: rg.name,
          },
        });
      });

      let newSelectedRoleGroupIds: number[] = [];
      if (props.guestClientAssignedRoleGroups && clientId == props.guestClientId) {
        newSelectedRoleGroupIds = newAvailableRoleGroups.filter(x => props.guestClientAssignedRoleGroups.includes(x.roleGroupId)).map(x => x.roleGroupId);
      }

      setAvailableRoleGroupOptions(roleGroupOptions);
      setAvailableRoleGroups(newAvailableRoleGroups);
      setSelectedRoleGroupIds(newSelectedRoleGroupIds);

      createRolePreviewWafflesTable(selectedRoles, newSelectedRoleGroupIds);
    });
  }

  // either get the currently assigned roles for the existing guest login or set an empty list of assigned roles if its a new guest login
  const updateGuestLoginRoles = (clientId: number) => {
    // if the guest client id is the selected client id, pre-load the currently selected roles (i.e., we are editing this guest login)
    let newSelectedRoles: string[];
    if (props.guestClientAssignedRoles && clientId == props.guestClientId) {
      newSelectedRoles = orderBy(props.guestClientAssignedRoles, (r) => r.toLowerCase(), "asc");
    } else { // clear the list of selected roles since we're adding a new guest login
      newSelectedRoles = userDefaultRoles;
    }

    setSelectedRoles(newSelectedRoles);
    createRolePreviewWafflesTable(newSelectedRoles, selectedRoleGroupIds);
  }

  const updateRoles = (response: MultiselectOnChangeResponse) => {
    const newSelectedRoles = orderBy(response.checkedKeys, (r) => r.toLowerCase(), "asc");
    setSelectedRoles(newSelectedRoles);
  }

  const updateRoleGroups = (response: MultiselectOnChangeResponse) => {
    const selectedRoleGroupOptions = response.checkedKeys;
    const newSelectedRoleGroupIds = availableRoleGroups.filter(x => selectedRoleGroupOptions.includes(x.name)).map(x => x.roleGroupId);
    setSelectedRoleGroupIds(newSelectedRoleGroupIds);
  }

  return (
    <>
      <div className="row mb-4">
        <QESSelect
          containerClassName="col-12"
          label="Client"
          onChange={handleClientChange}
          testId="guest-login__client"
          disabled={props.guestClientId > 0}
        >
          {clientOptions}
        </QESSelect>
      </div>
      {selectedClientId > 0 && availableRoles && (
        <div className="row">
          <div className="col-12">
            <QESMultiSelect
              containerClassName="col-8 mb-3"
              label="Authorized Role Groups"
              onChange={updateRoleGroups}
              options={availableRoleGroupOptions}
              prefix="roleGroups"
              testId="guest-login__role-groups"
              validationTooltipContent="User must be in at least 1 role either directly or via a role group."
            />
            <QESMultiSelect
              containerClassName="col-8 mb-3"
              errorText={`At least one role or role group must be selected.`}
              label="Individually Assigned Roles"
              onChange={updateRoles}
              options={availableRoles}
              required={true}
              isValid={isRoleValid()}
              prefix="roles"
              testId="guest-login__roles"
              validationTooltipContent={`User must be in at least 1 role either directly or via a role group.`}
            />
            <QESButton
              qesTestId="default-useradmin-SelectAll"
              qesType="secondary"
              onClick={() => { setSelectedRoles(availableRoleNames) }}>
              Select All</QESButton>
            <QESButton
              qesTestId="default-useradmin-ClearAll"
              qesType="secondary"
              onClick={() => { setSelectedRoles([]); }}>
              Clear All</QESButton>
            <QESButton
              qesTestId="default-useradmin-DefaultUser"
              qesType="secondary"
              onClick={() => { setSelectedRoles(userDefaultRoles); }}>
              Use Default User Roles</QESButton>
            <QESButton
              qesTestId="default-useradmin-ResourceAdminRoles"
              qesType="secondary"
              onClick={() => { setSelectedRoles(resourceAdminRoles); }}>
              Use Resource Admin Roles</QESButton>
            <QESButton
              qesTestId="default-useradmin-QuestViewerRoles"
              qesType="secondary"
              onClick={() => { setSelectedRoles(questViewerRoles); }}>
              Use Quest Viewer Roles</QESButton>
          </div>
        </div>
      )}
      <AccordionContainer className="mt-3">
        <Accordion title="Role Preview" testId="guest-login-admin-panel-content__role-preview-accordion">
          <Waffles<RolePreviewTableData>
            columns={rolePreviewColumnConfig}
            data={rolePreview}
            fullWidth={true}
            testId="role-preview-waffles-table"
            emptyTableMessage="There are no roles selected for this user."
          />
        </Accordion>
      </AccordionContainer>

      <FlyoutFooter
        submitButton={<QESButton qesTestId="guest-login-admin-panel-content_save" className="mt10" disabled={!isValid()} onClick={handleOnClick} > Save </QESButton>}
      />
    </>
  );
}
